import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Building2,
  Folder,
  GitMerge,
  Layers,
  Plus,
  Trash2,
  Undo2,
  UserRound,
  X,
} from "lucide-react";
import { api, ApiError } from "../../api";
import { Card } from "../../ui";

/**
 * Unit detail surface reachable from /admin/units. Pulls the unit's
 * profile (riser + building) plus its active owners and the full
 * project history for the unit. Owner names link to the user-detail
 * page so a PM can pivot from a noisy unit to the human running the
 * projects.
 *
 * Building-scoped on the server: the principal must be a PM/admin
 * at the unit's building. A 403 from the API surfaces as a friendly
 * banner rather than an error throw.
 *
 * From here PMs/admins can also: assign and remove unit owners
 * (POST /api/admin/units/:unitId/owners, DELETE …/:ownershipId), and
 * combine this unit with one or more sibling units into a new
 * combined unit (POST /api/admin/buildings/:buildingId/units/combine).
 * The combine flow archives the source units and re-points their
 * ownerships + projects onto the new unit; the originals stay in the
 * DB but are filtered out of active listings.
 */

type UnitDetail = {
  unit: {
    id: string;
    unitNumber: string;
    floor: number | null;
    unitType: string | null;
    location: string | null;
    isCombinedUnit: boolean;
    hasWasherDryer: boolean;
    buildingId: string;
    buildingName: string;
    riserId: string;
    riserCode: string;
    riserName: string;
    archivedAt: string | null;
    mergedIntoUnitId: string | null;
    mergedInto: { id: string; unitNumber: string } | null;
    mergedFrom: Array<{ id: string; unitNumber: string }>;
    additionalRisers: Array<{ id: string; code: string; name: string }>;
    createdAt: string;
  };
  owners: Array<{
    ownershipId: string;
    userId: string;
    name: string;
    email: string;
    ownershipType: string;
    startedAt: string;
  }>;
  projects: Array<{
    id: string;
    title: string;
    statusCode: string;
    projectTypeName: string;
    submittedById: string;
    submittedByName: string;
    submittedOnBehalfOfUserId: string | null;
    submittedAt: string | null;
    createdAt: string;
    approvedAt: string | null;
    closedAt: string | null;
  }>;
};

type UserSearchRow = {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
};

type UnitListRow = {
  id: string;
  unitNumber: string;
  floor: number | null;
  riserId: string;
  riserCode: string;
  isCombinedUnit: boolean;
};

const TYPE_LABEL: Record<string, string> = {
  studio: "Studio",
  one_bedroom: "1 Bedroom",
  two_bedroom: "2 Bedroom",
  three_bedroom: "3 Bedroom",
};
const TYPE_OPTIONS = [
  { value: "", label: "—" },
  { value: "studio", label: "Studio" },
  { value: "one_bedroom", label: "1 Bedroom" },
  { value: "two_bedroom", label: "2 Bedroom" },
  { value: "three_bedroom", label: "3 Bedroom" },
];

const LOCATION_LABEL: Record<string, string> = {
  east: "East",
  north: "North",
  northeast_corner: "Northeast Corner",
  northwest_corner: "Northwest Corner",
  south: "South",
  southeast_corner: "Southeast Corner",
  southwest_corner: "Southwest Corner",
  west: "West",
};
const LOCATION_OPTIONS = [
  { value: "", label: "—" },
  ...Object.entries(LOCATION_LABEL).map(([value, label]) => ({ value, label })),
];

const OWNERSHIP_TYPE_OPTIONS = [
  { value: "primary", label: "Primary owner" },
  { value: "co_owner", label: "Co-owner" },
  { value: "investor", label: "Investor" },
];

export function AdminUnitDetail() {
  const { unitId = "" } = useParams<{ unitId: string }>();
  const [data, setData] = useState<UnitDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const reload = () => setReloadKey((k) => k + 1);

  useEffect(() => {
    setData(null);
    setError(null);
    api
      .get<{ data: UnitDetail }>(`/api/admin/units/${unitId}`)
      .then((r) => setData(r.data))
      .catch((err) => {
        if (err instanceof ApiError) {
          setError(
            err.status === 403
              ? "You don't have access to this unit."
              : err.status === 404
                ? "Unit not found."
                : `Load failed (${err.status})`,
          );
        } else setError(String(err));
      });
  }, [unitId, reloadKey]);

  const archived = !!data?.unit.archivedAt;

  return (
    <div>
      <div className="-mt-6 -mx-4 sm:-mx-6 lg:-mx-8 mb-6 bg-brand-600 text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 pt-5 pb-7">
          <Link
            to="/admin/units"
            className="inline-flex items-center gap-1 text-[12px] text-white/70 hover:text-white no-underline hover:no-underline mb-3"
          >
            <ArrowLeft size={13} /> All units
          </Link>
          <h1 className="text-white m-0">
            {data ? `Unit ${data.unit.unitNumber}` : "Unit"}
          </h1>
          {data && (
            <p className="mt-1 text-[12.5px] text-white/70 m-0">
              {data.unit.buildingName}
            </p>
          )}
        </div>
      </div>

      {error && (
        <Card>
          <p className="text-status-rejected-fg text-sm m-0">{error}</p>
        </Card>
      )}

      {!error && !data && (
        <Card>
          <p className="text-ink-500 text-sm m-0">Loading…</p>
        </Card>
      )}

      {data && archived && data.unit.mergedInto && (
        <Card className="mb-4 border-amber-300 bg-amber-50">
          <p className="text-[13px] text-ink-700 m-0">
            <strong>This unit was combined</strong> into{" "}
            <Link
              to={`/admin/units/${data.unit.mergedInto.id}`}
              className="text-brand-700 hover:text-brand-800 no-underline hover:no-underline"
            >
              Unit {data.unit.mergedInto.unitNumber}
            </Link>
            . Its ownerships and projects were re-pointed; this record is
            kept for audit.
          </p>
        </Card>
      )}

      {data && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
          <Card>
            <h3 className="text-ink-800 m-0 mb-3 text-[15px] inline-flex items-center gap-2">
              <Building2 size={14} className="text-ink-500" />
              Profile
            </h3>
            <dl className="grid grid-cols-[120px_1fr] gap-y-2 gap-x-3 text-[13px] m-0">
              <Label>Building</Label>
              <Value>{data.unit.buildingName}</Value>
              <Label>Floor</Label>
              <Value>{data.unit.floor ?? "—"}</Value>
              <Label>Type</Label>
              <Value>
                {data.unit.unitType
                  ? (TYPE_LABEL[data.unit.unitType] ?? data.unit.unitType)
                  : "—"}
              </Value>
              <Label>Location</Label>
              <Value>
                {data.unit.location
                  ? (LOCATION_LABEL[data.unit.location] ?? data.unit.location)
                  : "—"}
              </Value>
              <Label>Combined</Label>
              <Value>{data.unit.isCombinedUnit ? "yes" : "no"}</Value>
              <Label>W/D hookup</Label>
              <Value>{data.unit.hasWasherDryer ? "yes" : "no"}</Value>
              {data.unit.mergedFrom.length > 0 && (
                <>
                  <Label>Combined from</Label>
                  <Value>
                    {data.unit.mergedFrom.map((u, i) => (
                      <span key={u.id}>
                        {i > 0 && ", "}
                        <Link
                          to={`/admin/units/${u.id}`}
                          className="text-brand-700 hover:text-brand-800 no-underline hover:no-underline"
                        >
                          Unit {u.unitNumber}
                        </Link>
                      </span>
                    ))}
                  </Value>
                </>
              )}
            </dl>
          </Card>

          <Card>
            <h3 className="text-ink-800 m-0 mb-3 text-[15px] inline-flex items-center gap-2">
              <Layers size={14} className="text-ink-500" />
              Riser{data.unit.additionalRisers.length > 0 ? "s" : ""}
            </h3>
            <dl className="grid grid-cols-[120px_1fr] gap-y-2 gap-x-3 text-[13px] m-0">
              <Label>Code</Label>
              <Value>
                <span className="font-mono">{data.unit.riserCode}</span>
                {data.unit.additionalRisers.map((r) => (
                  <span key={r.id} className="font-mono ml-1.5">
                    , {r.code}
                  </span>
                ))}
              </Value>
              <Label>Name</Label>
              <Value>
                {data.unit.additionalRisers.length === 0
                  ? data.unit.riserName
                  : [data.unit.riserName, ...data.unit.additionalRisers.map((r) => r.name)].join(", ")}
              </Value>
              {data.unit.additionalRisers.length > 0 && (
                <>
                  <Label>{" "}</Label>
                  <Value>
                    <p className="text-[11.5px] text-ink-400 m-0">
                      First riser is the primary. Shutdown
                      notifications and per-riser W/D capacity still
                      bind to the primary only.
                    </p>
                  </Value>
                </>
              )}
            </dl>
          </Card>

          <OwnersCard
            unitId={data.unit.id}
            owners={data.owners}
            archived={archived}
            onChanged={reload}
          />

          {!archived && data.unit.isCombinedUnit ? (
            <UncombineCard
              unit={data.unit}
              onUncombined={(primarySourceUnitId) => {
                window.location.href = `/admin/units/${primarySourceUnitId}`;
              }}
            />
          ) : !archived ? (
            <CombineUnitsCard
              unit={data.unit}
              onCombined={(newUnitId) => {
                window.location.href = `/admin/units/${newUnitId}`;
              }}
            />
          ) : null}
        </div>
      )}

      {data && (
        <Card className="!p-0 overflow-hidden">
          <div className="flex items-center gap-2 px-5 py-4 border-b border-sand-100">
            <Folder size={14} className="text-ink-500" />
            <h3 className="text-ink-800 m-0 text-[15px]">Projects</h3>
            <span className="text-ink-400 text-[12px]">
              {data.projects.length} total
            </span>
          </div>
          {data.projects.length === 0 ? (
            <p className="p-5 text-ink-500 text-[13px] m-0">
              No projects on file for this unit.
            </p>
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Title</th>
                    <th>Type</th>
                    <th>Status</th>
                    <th>Submitted by</th>
                    <th>Created</th>
                    <th>Closed</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {data.projects.map((p) => (
                    <tr key={p.id}>
                      <td className="font-medium text-ink-800">{p.title}</td>
                      <td className="text-ink-600">{p.projectTypeName}</td>
                      <td>
                        <span className="badge bg-sand-100 text-ink-700">
                          {p.statusCode.replace(/_/g, " ")}
                        </span>
                      </td>
                      <td className="text-[12.5px]">
                        <Link
                          to={`/admin/users/${p.submittedById}`}
                          className="text-brand-700 hover:text-brand-800 no-underline hover:no-underline"
                        >
                          {p.submittedByName}
                        </Link>
                        {p.submittedOnBehalfOfUserId && (
                          <span className="text-[11px] text-ink-400 ml-1">
                            (white-glove)
                          </span>
                        )}
                      </td>
                      <td className="text-[12px] text-ink-500">
                        {new Date(p.createdAt).toLocaleDateString()}
                      </td>
                      <td className="text-[12px] text-ink-500">
                        {p.closedAt
                          ? new Date(p.closedAt).toLocaleDateString()
                          : "—"}
                      </td>
                      <td>
                        <Link
                          to={`/projects/${p.id}`}
                          className="text-brand-700 hover:text-brand-800 text-[12.5px] no-underline hover:no-underline"
                        >
                          Open
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Owners section
//
// Renders active owners + an inline assign form. The assign form
// type-aheads against /api/admin/users?q=… and calls
// POST /api/admin/units/:unitId/owners. Remove uses
// DELETE /api/admin/units/:unitId/owners/:ownershipId. Both surface
// errors as a small inline banner. After a successful action we call
// onChanged() so the parent re-fetches the detail payload.
// ---------------------------------------------------------------------------
function OwnersCard({
  unitId,
  owners,
  archived,
  onChanged,
}: {
  unitId: string;
  owners: UnitDetail["owners"];
  archived: boolean;
  onChanged: () => void;
}) {
  const [adding, setAdding] = useState(false);
  return (
    <Card className="lg:col-span-2">
      <div className="flex items-center gap-2 mb-3">
        <h3 className="text-ink-800 m-0 text-[15px] inline-flex items-center gap-2">
          <UserRound size={14} className="text-ink-500" />
          Owners
        </h3>
        {!archived && !adding && (
          <button
            className="ml-auto btn-secondary btn-sm"
            onClick={() => setAdding(true)}
          >
            <Plus size={13} /> Assign owner
          </button>
        )}
      </div>

      {adding && (
        <AssignOwnerForm
          unitId={unitId}
          onCancel={() => setAdding(false)}
          onAssigned={() => {
            setAdding(false);
            onChanged();
          }}
        />
      )}

      {owners.length === 0 ? (
        <p className="text-ink-500 text-[13px] m-0">
          No active owners assigned.
        </p>
      ) : (
        <ul className="divide-y divide-sand-100 m-0 p-0 list-none">
          {owners.map((o) => (
            <OwnerRow
              key={o.ownershipId}
              unitId={unitId}
              owner={o}
              archived={archived}
              onRemoved={onChanged}
            />
          ))}
        </ul>
      )}
    </Card>
  );
}

function OwnerRow({
  unitId,
  owner,
  archived,
  onRemoved,
}: {
  unitId: string;
  owner: UnitDetail["owners"][number];
  archived: boolean;
  onRemoved: () => void;
}) {
  const [removing, setRemoving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const remove = async () => {
    if (
      !window.confirm(
        `End ${owner.name}'s ownership of this unit? This is reversible by re-assigning them.`,
      )
    ) {
      return;
    }
    setRemoving(true);
    setError(null);
    try {
      await api.del(
        `/api/admin/units/${unitId}/owners/${owner.ownershipId}`,
      );
      onRemoved();
    } catch (err) {
      setError(err instanceof ApiError ? `Failed (${err.status})` : String(err));
      setRemoving(false);
    }
  };

  return (
    <li className="py-2 flex items-center gap-3 flex-wrap">
      <Link
        to={`/admin/users/${owner.userId}`}
        className="font-medium text-ink-800 hover:text-brand-700 no-underline hover:no-underline"
        title="Open this owner's full record"
      >
        {owner.name}
      </Link>
      <span className="text-[12px] text-ink-500">{owner.email}</span>
      <span className="badge bg-sand-100 text-ink-600">
        {owner.ownershipType}
      </span>
      <span className="ml-auto text-[11px] text-ink-400">
        since {new Date(owner.startedAt).toLocaleDateString()}
      </span>
      {!archived && (
        <button
          className="btn-ghost btn-sm text-status-rejected-fg"
          onClick={remove}
          disabled={removing}
          title="End this ownership"
        >
          <Trash2 size={13} />
        </button>
      )}
      {error && (
        <span className="text-[11.5px] text-status-rejected-fg w-full">
          {error}
        </span>
      )}
    </li>
  );
}

// Inline assign form. Uses /api/admin/users?q= for the type-ahead.
// We debounce the query 200ms so a fast typist doesn't fan out one
// fetch per keystroke. The picked user is locked in (no fuzzy
// "looked like a match" behavior); the admin must choose a row from
// the dropdown.
function AssignOwnerForm({
  unitId,
  onAssigned,
  onCancel,
}: {
  unitId: string;
  onAssigned: () => void;
  onCancel: () => void;
}) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<UserSearchRow[]>([]);
  const [picked, setPicked] = useState<UserSearchRow | null>(null);
  const [ownershipType, setOwnershipType] = useState("primary");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (picked) return;
    const q = query.trim();
    if (q.length < 2) {
      setResults([]);
      return;
    }
    const t = setTimeout(() => {
      api
        .get<{ data: UserSearchRow[] }>(
          `/api/admin/users?q=${encodeURIComponent(q)}`,
        )
        .then((r) => setResults(r.data.slice(0, 10)))
        .catch(() => setResults([]));
    }, 200);
    return () => clearTimeout(t);
  }, [query, picked]);

  const submit = async () => {
    if (!picked) return;
    setSubmitting(true);
    setError(null);
    try {
      await api.post(`/api/admin/units/${unitId}/owners`, {
        userId: picked.id,
        ownershipType,
      });
      onAssigned();
    } catch (err) {
      const m =
        err instanceof ApiError
          ? typeof err.body === "object" && err.body && "error" in err.body
            ? String((err.body as { error: string }).error).replace(
                /_/g,
                " ",
              )
            : `HTTP ${err.status}`
          : String(err);
      setError(m);
      setSubmitting(false);
    }
  };

  return (
    <div className="mb-4 p-3 rounded-md border border-sand-200 bg-sand-50 flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <strong className="text-[13px] text-ink-800">Assign owner</strong>
        <button
          className="ml-auto btn-ghost btn-sm"
          onClick={onCancel}
          aria-label="Close"
        >
          <X size={13} />
        </button>
      </div>

      {!picked ? (
        <div className="relative">
          <input
            type="search"
            autoFocus
            placeholder="Search by name or email…"
            className="input h-[34px]"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          {results.length > 0 && (
            <ul className="absolute z-10 left-0 right-0 mt-1 bg-white border border-sand-200 rounded-md shadow-pop p-1 list-none m-0 max-h-64 overflow-auto">
              {results.map((u) => (
                <li key={u.id}>
                  <button
                    type="button"
                    className="w-full text-left px-2 py-1.5 rounded text-[13px] hover:bg-sand-100"
                    onClick={() => {
                      setPicked(u);
                      setResults([]);
                      setQuery("");
                    }}
                  >
                    <span className="font-medium text-ink-800">
                      {u.firstName} {u.lastName}
                    </span>
                    <span className="text-ink-500 ml-2 text-[12px]">
                      {u.email}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          {query.trim().length >= 2 && results.length === 0 && (
            <p className="text-[12px] text-ink-500 mt-1 m-0">No matches.</p>
          )}
        </div>
      ) : (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="badge bg-white border border-sand-200 text-ink-700">
            {picked.firstName} {picked.lastName} · {picked.email}
          </span>
          <button
            type="button"
            className="btn-ghost btn-sm"
            onClick={() => setPicked(null)}
          >
            Change
          </button>
        </div>
      )}

      <div className="flex items-center gap-2 flex-wrap">
        <label className="text-[12px] text-ink-600">Type</label>
        <select
          className="select h-[32px] py-0"
          value={ownershipType}
          onChange={(e) => setOwnershipType(e.target.value)}
        >
          {OWNERSHIP_TYPE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <button
          className="btn-primary btn-sm ml-auto"
          onClick={submit}
          disabled={!picked || submitting}
        >
          Assign
        </button>
      </div>

      {error && (
        <p className="text-[12px] text-status-rejected-fg m-0">{error}</p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Combine units
//
// Inline form that loads the building's active units (filtered to the
// same building, excluding this unit and any already-archived units),
// lets the admin multi-select sibling units, and submits a new unit
// description. The server creates the new combined unit, re-points
// ownerships and projects, archives the sources, and returns the new
// unit's id which we navigate to so the admin lands on the merged
// record.
// ---------------------------------------------------------------------------
function CombineUnitsCard({
  unit,
  onCombined,
}: {
  unit: UnitDetail["unit"];
  onCombined: (newUnitId: string) => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <Card>
      <div className="flex items-center gap-2 mb-2">
        <h3 className="text-ink-800 m-0 text-[15px] inline-flex items-center gap-2">
          <GitMerge size={14} className="text-ink-500" />
          Combine with another unit
        </h3>
      </div>
      <p className="text-[12.5px] text-ink-500 m-0 mb-3">
        Creates a new combined unit (e.g. "{unit.unitNumber}–neighbor"),
        moves ownerships and project history onto it, and archives the
        originals. Audited.
      </p>
      {open ? (
        <CombineForm
          unit={unit}
          onCancel={() => setOpen(false)}
          onCombined={onCombined}
        />
      ) : (
        <button className="btn-secondary btn-sm" onClick={() => setOpen(true)}>
          <GitMerge size={13} /> Combine units…
        </button>
      )}
    </Card>
  );
}

function CombineForm({
  unit,
  onCancel,
  onCombined,
}: {
  unit: UnitDetail["unit"];
  onCancel: () => void;
  onCombined: (newUnitId: string) => void;
}) {
  const [siblings, setSiblings] = useState<UnitListRow[] | null>(null);
  const [partnerIds, setPartnerIds] = useState<string[]>([]);
  const [unitNumber, setUnitNumber] = useState("");
  const [riserCode, setRiserCode] = useState(unit.riserCode);
  const [floor, setFloor] = useState<string>(
    unit.floor != null ? String(unit.floor) : "",
  );
  const [unitType, setUnitType] = useState<string>(unit.unitType ?? "");
  const [location, setLocation] = useState<string>(unit.location ?? "");
  const [hasWasherDryer, setHasWasherDryer] = useState(unit.hasWasherDryer);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Pre-fill the suggested unit number on first mount with the
  // simplest convention ("1108-09" — current number plus the partner's
  // last 2 digits). Keep it editable; admins routinely diverge.
  const filledOnce = useRef(false);

  useEffect(() => {
    api
      .get<{ data: UnitListRow[] }>(
        `/api/admin/buildings/${unit.buildingId}/units`,
      )
      .then((r) =>
        setSiblings(r.data.filter((u) => u.id !== unit.id)),
      )
      .catch(() => setSiblings([]));
  }, [unit.buildingId, unit.id]);

  useEffect(() => {
    if (filledOnce.current || !siblings || partnerIds.length === 0) return;
    const partner = siblings.find((s) => s.id === partnerIds[0]);
    if (!partner) return;
    const tail = partner.unitNumber.slice(-2);
    setUnitNumber(`${unit.unitNumber}-${tail}`);
    filledOnce.current = true;
  }, [partnerIds, siblings, unit.unitNumber]);

  const togglePartner = (id: string) =>
    setPartnerIds((prev) =>
      prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id],
    );

  const groupedSiblings = useMemo(() => {
    if (!siblings) return [];
    return [...siblings].sort((a, b) =>
      a.unitNumber.localeCompare(b.unitNumber),
    );
  }, [siblings]);

  // Live preview of which risers the new combined unit will span,
  // beyond the primary riser the admin typed into "Primary riser
  // code". We dedupe across the current unit + every selected
  // partner and drop whichever riser matches the typed primary
  // (case-insensitive so "01" entered for a riser stored as "01"
  // still matches). When everything's on one riser the preview
  // says so; otherwise it lists the extras.
  const additionalRiserCodes = useMemo(() => {
    if (!siblings) return [];
    const primary = riserCode.trim().toLowerCase();
    const codes = new Set<string>();
    codes.add(unit.riserCode);
    for (const id of partnerIds) {
      const s = siblings.find((u) => u.id === id);
      if (s) codes.add(s.riserCode);
    }
    return Array.from(codes)
      .filter((c) => c.toLowerCase() !== primary)
      .sort();
  }, [siblings, partnerIds, riserCode, unit.riserCode]);

  const submit = async () => {
    if (partnerIds.length === 0) {
      setError("Pick at least one unit to combine with.");
      return;
    }
    if (!unitNumber.trim()) {
      setError("New unit number is required.");
      return;
    }
    if (
      !window.confirm(
        `Combine ${1 + partnerIds.length} units into a new unit "${unitNumber.trim()}"? Ownerships and projects from the originals will move onto the new unit.`,
      )
    ) {
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const r = await api.post<{ data: { id: string } }>(
        `/api/admin/buildings/${unit.buildingId}/units/combine`,
        {
          sourceUnitIds: [unit.id, ...partnerIds],
          newUnit: {
            unitNumber: unitNumber.trim(),
            riserCode: riserCode.trim(),
            floor: floor.trim() === "" ? undefined : Number(floor),
            unitType: unitType || undefined,
            location: location || undefined,
            hasWasherDryer,
          },
        },
      );
      onCombined(r.data.id);
    } catch (err) {
      const m =
        err instanceof ApiError
          ? typeof err.body === "object" && err.body && "error" in err.body
            ? String((err.body as { error: string }).error).replace(
                /_/g,
                " ",
              )
            : `HTTP ${err.status}`
          : String(err);
      setError(m);
      setSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <div>
        <div className="text-[12px] text-ink-600 mb-1">
          Combine with (pick one or more):
        </div>
        {!siblings ? (
          <p className="text-[12.5px] text-ink-500 m-0">Loading…</p>
        ) : groupedSiblings.length === 0 ? (
          <p className="text-[12.5px] text-ink-500 m-0">
            No other units in this building.
          </p>
        ) : (
          <div className="max-h-40 overflow-auto border border-sand-200 rounded-md bg-white">
            <ul className="m-0 p-0 list-none">
              {groupedSiblings.map((s) => (
                <li
                  key={s.id}
                  className="px-2 py-1.5 border-b border-sand-100 last:border-0 text-[13px] flex items-center gap-2"
                >
                  <input
                    type="checkbox"
                    id={`combine-${s.id}`}
                    checked={partnerIds.includes(s.id)}
                    onChange={() => togglePartner(s.id)}
                  />
                  <label
                    htmlFor={`combine-${s.id}`}
                    className="cursor-pointer flex-1"
                  >
                    <span className="font-medium text-ink-800">
                      Unit {s.unitNumber}
                    </span>
                    <span className="text-ink-500 ml-2 text-[12px]">
                      Floor {s.floor ?? "—"} · Riser {s.riserCode}
                      {s.isCombinedUnit && " · combined"}
                    </span>
                  </label>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-2">
        <label className="text-[12px] text-ink-600 col-span-2">
          New unit number
          <input
            className="input h-[34px] mt-1"
            value={unitNumber}
            onChange={(e) => setUnitNumber(e.target.value)}
            placeholder="e.g. 1108-09"
          />
        </label>
        <label className="text-[12px] text-ink-600">
          Primary riser code
          <input
            className="input h-[34px] mt-1 font-mono"
            value={riserCode}
            onChange={(e) => setRiserCode(e.target.value)}
          />
          {partnerIds.length > 0 && (
            <span className="block text-[11.5px] text-ink-500 mt-1">
              {additionalRiserCodes.length === 0
                ? "All selected units share this riser."
                : `Will also span: ${additionalRiserCodes.join(", ")}`}
            </span>
          )}
        </label>
        <label className="text-[12px] text-ink-600">
          Floor
          <input
            className="input h-[34px] mt-1"
            value={floor}
            onChange={(e) => setFloor(e.target.value)}
            inputMode="numeric"
          />
        </label>
        <label className="text-[12px] text-ink-600">
          Type
          <select
            className="select h-[34px] mt-1 py-0"
            value={unitType}
            onChange={(e) => setUnitType(e.target.value)}
          >
            {TYPE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        <label className="text-[12px] text-ink-600">
          Location
          <select
            className="select h-[34px] mt-1 py-0"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
          >
            {LOCATION_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        <label className="text-[12px] text-ink-600 col-span-2 inline-flex items-center gap-2">
          <input
            type="checkbox"
            checked={hasWasherDryer}
            onChange={(e) => setHasWasherDryer(e.target.checked)}
          />
          Has W/D hookup
        </label>
      </div>

      {error && (
        <p className="text-[12px] text-status-rejected-fg m-0">{error}</p>
      )}

      <div className="flex items-center gap-2">
        <button
          className="btn-primary btn-sm"
          onClick={submit}
          disabled={submitting}
        >
          Combine
        </button>
        <button className="btn-ghost btn-sm" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Uncombine
//
// Shown on a combined unit's detail page (unit.isCombinedUnit and not
// archived). Calls POST /api/admin/units/:unitId/uncombine. The
// server uses the audit manifest from the original combine to put
// projects, ownerships, and W/D counts back where they were and
// soft-archives the combined unit. We then navigate to the primary
// source unit so the admin lands on something that exists.
//
// Server may refuse for a few reasons surfaced inline:
//   - drift_owners / drift_projects: someone added rows to the
//     combined unit after combine. Admin needs to remove them
//     manually first.
//   - source_re_combined: a source unit was already combined again.
//     Undo the later combine first.
//   - no_combine_manifest: this unit predates the manifest schema or
//     the audit row was lost.
// ---------------------------------------------------------------------------
function UncombineCard({
  unit,
  onUncombined,
}: {
  unit: UnitDetail["unit"];
  onUncombined: (primarySourceUnitId: string) => void;
}) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [needsFallback, setNeedsFallback] = useState(false);
  const sources = unit.mergedFrom;

  const submit = async (acknowledgeImprecise: boolean) => {
    setSubmitting(true);
    setError(null);
    try {
      const r = await api.post<{
        data: {
          primarySourceUnitId: string;
          restoredSourceUnitIds: string[];
          mode: "manifest" | "fallback";
          projectsMovedCount?: number;
        };
      }>(
        `/api/admin/units/${unit.id}/uncombine`,
        acknowledgeImprecise ? { acknowledgeImprecise: true } : undefined,
      );
      onUncombined(r.data.primarySourceUnitId);
    } catch (err) {
      const code =
        err instanceof ApiError &&
        typeof err.body === "object" &&
        err.body &&
        "error" in err.body
          ? String((err.body as { error: string }).error)
          : null;
      if (code === "no_combine_manifest") {
        // First-time fallback discovery: show the imprecise-mode
        // controls inline rather than as an error so the admin
        // understands what the trade-off is before they retry.
        setNeedsFallback(true);
        setError(null);
      } else {
        setError(
          code === "drift_owners"
            ? "Owners were added or changed since the combine. Remove the new owners on this unit first, then retry."
            : code === "drift_projects"
              ? "New projects were created on this combined unit since the combine. Move or close them first, then retry."
              : code === "source_re_combined"
                ? "A source unit was combined again into a newer unit. Undo that one first."
                : code === "no_sources_found"
                  ? "No source units could be inferred. This combined unit can't be undone here."
                  : code === "already_archived"
                    ? "This unit is already archived."
                    : code === "not_a_combined_unit"
                      ? "This isn't a combined unit."
                      : code
                        ? code.replace(/_/g, " ")
                        : err instanceof ApiError
                          ? `HTTP ${err.status}`
                          : String(err),
        );
      }
      setSubmitting(false);
    }
  };

  const undoPrecise = () => {
    if (
      !window.confirm(
        `Undo the combine? This will move ownerships and projects back to the original ${sources.length || "source"} unit${sources.length === 1 ? "" : "s"} and archive this combined unit. The action is itself audited.`,
      )
    ) {
      return;
    }
    void submit(false);
  };

  const undoFallback = () => {
    if (
      !window.confirm(
        `Undo with best-effort recovery? This combine predates the precise undo manifest, so:\n\n• Every project currently on this combined unit will be moved to the FIRST source unit (you may need to manually re-assign them afterwards).\n• Ownerships ended at combine time will be revived; new ownerships on this unit will be ended.\n• The combined unit will be archived.\n\nProceed?`,
      )
    ) {
      return;
    }
    void submit(true);
  };

  return (
    <Card>
      <div className="flex items-center gap-2 mb-2">
        <h3 className="text-ink-800 m-0 text-[15px] inline-flex items-center gap-2">
          <Undo2 size={14} className="text-ink-500" />
          Undo combine
        </h3>
      </div>
      <p className="text-[12.5px] text-ink-500 m-0 mb-3">
        Reverses the combine that produced Unit {unit.unitNumber}. Owners,
        projects, and W/D counts are restored to the original source
        units; this combined unit is archived. Audited.
      </p>
      {sources.length > 0 ? (
        <p className="text-[12.5px] text-ink-600 m-0 mb-3">
          Will restore:{" "}
          {sources.map((s, i) => (
            <span key={s.id}>
              {i > 0 && ", "}
              <span className="font-medium">Unit {s.unitNumber}</span>
            </span>
          ))}
        </p>
      ) : (
        <p className="text-[12.5px] text-status-rejected-fg m-0 mb-3">
          No source units found. This combined unit can't be undone here.
        </p>
      )}

      {!needsFallback ? (
        <button
          className="btn-danger btn-sm"
          onClick={undoPrecise}
          disabled={submitting || sources.length === 0}
        >
          <Undo2 size={13} /> Undo combine
        </button>
      ) : (
        <div className="rounded-md border border-amber-300 bg-amber-50 p-3">
          <p className="text-[12.5px] text-ink-700 m-0 mb-2">
            <strong>This combine predates the undo manifest.</strong> A
            best-effort recovery is available, but project lineage may
            not be preserved exactly: every project on this unit will
            be moved to the first source unit. You may need to
            re-assign them manually afterwards.
          </p>
          <div className="flex items-center gap-2">
            <button
              className="btn-danger btn-sm"
              onClick={undoFallback}
              disabled={submitting || sources.length === 0}
            >
              <Undo2 size={13} /> Undo (best-effort)
            </button>
            <button
              className="btn-ghost btn-sm"
              onClick={() => setNeedsFallback(false)}
              disabled={submitting}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {error && (
        <p className="text-[12px] text-status-rejected-fg m-0 mt-2">{error}</p>
      )}
    </Card>
  );
}

function Label({ children }: { children: React.ReactNode }) {
  return (
    <dt className="text-[11.5px] uppercase tracking-wide text-ink-400">
      {children}
    </dt>
  );
}
function Value({ children }: { children: React.ReactNode }) {
  return <dd className="text-ink-700 m-0">{children}</dd>;
}
